/**
 * Scio3WelcomeFlow — One-time welcome popup for users migrating to OptiSigns 3.0
 *
 * Light overlay on the REAL portal (fully visible behind) introducing the
 * Next-Gen Portal, OptiBot, and Global Search. Dismisses to the live app.
 *
 * Gated behind two backend signals so it can be killed instantly without a deploy:
 *   1. appCfgs.newLookPopupEnabled  — master toggle in MongoDB
 *   2. me.userSettings.showNewUIMessage  — per-user flag set by scio1's
 *      "Try New Interface" handoff; cleared on dismiss via UPDATE_USER_SETTINGS
 *
 * Mount inside AppShell (not a separate route). The real app stays fully functional.
 */

import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@apollo/client/react';
import { Bot, ExternalLink, Search, Sparkles, X } from 'lucide-react';
import { Button } from '@scio3/ui';
import { GET_APP_CFG, UPDATE_USER_SETTINGS } from '@scio3/graphql-client';
import { useAuthStore } from '@/stores/auth-store';
import { useTeamStore } from '@/stores/team-store';
import type { LucideIcon } from 'lucide-react';

// ---------------------------------------------------------------------------
// Types & constants
// ---------------------------------------------------------------------------

const LS_WELCOME_KEY = 'optisigns_3_welcome_seen';

/** Response shape for GET_APP_CFG with appType: 'scio' */
interface ScioAppCfgResponse {
  getAppCfgs: {
    newLookPopupEnabled?: boolean;
  } | null;
}

interface Scio3WelcomeFlowProps {
  forceShow?: boolean;
  onComplete?: () => void;
}

// ---------------------------------------------------------------------------
// localStorage helpers
// ---------------------------------------------------------------------------

function hasSeenWelcome(): boolean {
  try {
    return localStorage.getItem(LS_WELCOME_KEY) === 'true';
  } catch {
    return false;
  }
}

function markWelcomeSeen(): void {
  try {
    localStorage.setItem(LS_WELCOME_KEY, 'true');
  } catch {
    /* noop */
  }
}

// ---------------------------------------------------------------------------
// CSS keyframe animations
// ---------------------------------------------------------------------------

const KEYFRAMES_CSS = `
@keyframes wf-fadeSlideUp {
  from { opacity: 0; transform: translateY(20px) scale(0.98); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
}
@keyframes wf-backdropIn {
  from { opacity: 0; }
  to   { opacity: 1; }
}
@keyframes wf-previewSwap {
  from { opacity: 0; transform: translateY(12px) scale(0.97); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
}
`;

// ---------------------------------------------------------------------------
// Welcome modal
// ---------------------------------------------------------------------------

const FEATURE_PREVIEW_SRCS: Record<string, string> = {
  newLook: '/welcome/light-dark.avif',
  optibot: '/welcome/optibot.avif',
  search: '/welcome/search.avif',
};

/**
 * Mini preview panels for each feature — shown on the right side.
 * Module-level guard ensures we inject <link rel="preload"> tags into <head>
 * exactly once. The browser then has the strongest possible signal to fetch
 * every preview image up front, so the active image's rise-up animation
 * always plays against a fully-loaded element regardless of network speed.
 */
let previewPreloadStarted = false;
function preloadPreviewImages() {
  if (previewPreloadStarted || typeof document === 'undefined') return;
  previewPreloadStarted = true;
  for (const src of Object.values(FEATURE_PREVIEW_SRCS)) {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = src;
    document.head.appendChild(link);
  }
}

function FeaturePreview({ featureKey, alt }: { featureKey: string; alt: string }) {
  const src = FEATURE_PREVIEW_SRCS[featureKey] ?? FEATURE_PREVIEW_SRCS.newLook;
  useEffect(preloadPreviewImages, []);
  return (
    <div className="relative w-full" style={{ aspectRatio: '16 / 10' }}>
      <img
        key={featureKey}
        src={src}
        alt={alt}
        className="absolute inset-0 h-full w-full rounded-lg object-contain"
        style={{
          animation: 'wf-previewSwap 0.45s cubic-bezier(0.16, 1, 0.3, 1) both',
        }}
      />
    </div>
  );
}

function WelcomeModalD({ onDismiss }: { onDismiss: () => void }) {
  const { t } = useTranslation();
  const [activeFeature, setActiveFeature] = useState(0);

  const features: {
    key: string;
    title: string;
    description: string;
    alt: string;
    Icon: LucideIcon;
  }[] = [
    {
      key: 'newLook',
      title: t('Next-Gen Portal'),
      description: t("A redesigned interface that's faster, cleaner, with light & dark mode."),
      alt: t('New look preview'),
      Icon: Sparkles,
    },
    {
      key: 'optibot',
      title: t('OptiBot AI-Powered Assistant'),
      description: t(
        'Now takes action for you. Push content, update playlists, and manage screens directly from chat.',
      ),
      alt: t('OptiBot AI assistant preview'),
      Icon: Bot,
    },
    {
      key: 'search',
      title: t('Global Search'),
      description: t(
        'Find screens, assets, playlists, and schedules across your entire account instantly.',
      ),
      alt: t('Global search preview'),
      Icon: Search,
    },
  ];

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center overflow-hidden bg-black/30"
      style={{ animation: 'wf-backdropIn 0.3s ease-out' }}
      role="dialog"
      aria-modal="true"
      aria-label={t('Welcome to the New OptiSigns')}
    >
      <div
        className="relative mx-4 flex min-h-[460px] w-full origin-center scale-90 flex-col overflow-hidden rounded-xl bg-muted shadow-sm min-[900px]:mx-0 min-[900px]:aspect-[1000/460] min-[900px]:min-h-0 min-[900px]:w-[1000px] min-[900px]:flex-row min-[900px]:scale-[min(1.15,calc((100vw-32px)/1000px))]"
        style={{
          animation: 'wf-fadeSlideUp 0.6s cubic-bezier(0.16, 1, 0.3, 1)',
        }}
      >
        {/* Close X button */}
        <Button
          variant="ghost"
          size="icon-sm"
          onClick={onDismiss}
          aria-label={t('Close')}
          className="absolute right-3 top-3 z-20 rounded-full text-muted-foreground hover:bg-sidebar-active hover:text-foreground"
        >
          <X className="h-4 w-4" />
        </Button>

        {/* Left column — title, features, buttons (and image on mobile) */}
        <div
          className="flex w-full shrink-0 flex-col px-8 pb-6 pt-8 min-[900px]:w-[370px] min-[900px]:pl-8 min-[900px]:pr-2"
          style={{ minWidth: '0' }}
        >
          <h2
            className="text-2xl font-semibold text-foreground min-[900px]:text-xl"
            style={{ animation: 'wf-fadeSlideUp 0.4s ease-out 0.15s both' }}
          >
            {t('Welcome to the New OptiSigns')}
          </h2>
          <p
            className="mt-1 text-base text-muted-foreground min-[900px]:text-sm"
            style={{ animation: 'wf-fadeSlideUp 0.4s ease-out 0.2s both' }}
          >
            {t('Same powerful tools, modern foundation.')}
          </p>

          {/* Feature list
              Raw <button> intentionally used here — these are multi-line selector
              buttons with a large icon (36px) + wrapping description text. shadcn
              <Button> applies whitespace-nowrap and [&_svg]:size-4 that override
              both text wrapping and icon size, breaking the layout. */}
          <div className="mt-4 flex flex-1 flex-col justify-start gap-3 min-[900px]:gap-0 min-[1100px]:gap-3">
            {features.map((feature, i) => (
              <button
                key={feature.key}
                type="button"
                onClick={() => setActiveFeature(i)}
                className={`flex w-full cursor-pointer items-center gap-3 rounded-lg px-3 py-2 text-left transition-colors ${
                  activeFeature === i ? 'bg-sidebar-active' : 'hover:bg-sidebar-active/60'
                }`}
                style={{
                  animation: `wf-fadeSlideUp 0.4s ease-out ${0.25 + i * 0.08}s both`,
                }}
              >
                <feature.Icon
                  className="h-9 w-9 shrink-0 text-muted-foreground"
                  aria-hidden="true"
                />
                <div className="min-w-0 flex-1 space-y-1">
                  <p className="text-base font-medium leading-tight text-foreground min-[900px]:text-sm">
                    {feature.title}
                  </p>
                  <p className="text-sm leading-snug text-muted-foreground min-[900px]:text-xs">
                    {feature.description}
                  </p>
                </div>
              </button>
            ))}
          </div>

          {/* Mobile-only: image between buttons and footer */}
          <div className="mt-4 min-[900px]:hidden">
            <FeaturePreview
              featureKey={features[activeFeature].key}
              alt={features[activeFeature].alt}
            />
          </div>

          {/* Subtle note — between feature list and footer buttons */}
          <p
            className="mt-4 text-center text-xs italic leading-snug text-muted-foreground/80 min-[900px]:text-[11px]"
            style={{ animation: 'wf-fadeSlideUp 0.4s ease-out 0.55s both' }}
          >
            {t('Switch back to Classic anytime from your profile menu.')}
          </p>

          {/* Footer buttons — justified apart */}
          <div
            className="mt-4 flex flex-col-reverse items-stretch gap-2 min-[900px]:flex-row min-[900px]:items-center min-[900px]:justify-between"
            style={{ animation: 'wf-fadeSlideUp 0.4s ease-out 0.6s both' }}
          >
            <a
              href="https://www.optisigns.com/post/welcome-to-the-new-optisigns"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex h-9 cursor-pointer items-center justify-center gap-1.5 rounded-md px-4 text-base font-medium text-foreground transition-colors hover:bg-sidebar-active min-[900px]:text-sm"
            >
              {t('Learn More')}
              <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
            </a>
            <Button onClick={onDismiss} className="min-[900px]:text-sm">
              {t('Go to OptiSigns')}
            </Button>
          </div>
        </div>

        {/* Right — feature preview (desktop only, absolute bottom-right) */}
        <div
          className="pointer-events-none hidden min-[900px]:absolute min-[900px]:bottom-[-40px] min-[900px]:right-[-90px] min-[900px]:block min-[900px]:w-[75%]"
          style={{
            animation: 'wf-fadeSlideUp 0.5s ease-out 0.3s both',
          }}
        >
          <FeaturePreview
            featureKey={features[activeFeature].key}
            alt={features[activeFeature].alt}
          />
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component — mount inside AppShell
// ---------------------------------------------------------------------------

export default function Scio3WelcomeFlow({ forceShow, onComplete }: Scio3WelcomeFlowProps) {
  // Gate 1 — master toggle from MongoDB appCfg document (appType: 'scio').
  // When `newLookPopupEnabled` is false or missing, the popup never shows.
  const activeTeamId = useTeamStore((s) => s.activeTeamId);
  const { data: cfgData } = useQuery<ScioAppCfgResponse>(GET_APP_CFG, {
    variables: { teamId: activeTeamId, appType: 'scio' },
    skip: !activeTeamId,
    fetchPolicy: 'network-only',
  });
  const isPopupEnabled = cfgData?.getAppCfgs?.newLookPopupEnabled === true;

  // Gate 2 — per-user flag set by scio1 when the user clicks "Try New Interface".
  // scio1 writes `userSettings.showNewUIMessage = true` on switch to scio3.
  // We read it from ME_QUERY's userSettings JSON scalar, then clear it on dismiss.
  const me = useAuthStore((s) => s.me);
  const showNewUIMessage = me?.userSettings?.showNewUIMessage === true;
  const [updateUserSettings] = useMutation(UPDATE_USER_SETTINGS);

  const [showWelcome, setShowWelcome] = useState(() => forceShow || !hasSeenWelcome());

  // Lock body scroll while welcome modal is open
  useEffect(() => {
    if (!showWelcome) return;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, [showWelcome]);

  // Inject keyframes on mount, remove on unmount
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = KEYFRAMES_CSS;
    document.head.appendChild(style);
    return () => {
      style.remove();
    };
  }, []);

  /**
   * Persist the dismiss to the backend so this user never sees the popup again,
   * even on a different browser/device. Fire-and-forget — we don't block the
   * UI close on the network request, and we swallow errors since localStorage
   * acts as a local fallback.
   */
  const persistDismiss = useCallback(() => {
    void updateUserSettings({
      variables: {
        payload: { settings: { showNewUIMessage: false } },
        teamId: activeTeamId,
      },
    }).catch((error) => {
      console.error('Failed to persist welcome popup dismiss:', error);
    });
  }, [updateUserSettings, activeTeamId]);

  const handleDismiss = useCallback(() => {
    markWelcomeSeen();
    setShowWelcome(false);
    persistDismiss();
    onComplete?.();
  }, [onComplete, persistDismiss]);

  // Master kill switch — when the Mongo toggle is off, render nothing at all.
  if (!isPopupEnabled) return null;

  // Per-user gate — only show to users who scio1 explicitly flagged via
  // `userSettings.showNewUIMessage = true` (set when they click "Try New Interface").
  // `forceShow` (?welcome=1 URL param) is kept as an escape hatch for QA/dev.
  if (!showNewUIMessage && !forceShow) return null;

  if (!showWelcome) return null;

  return <WelcomeModalD onDismiss={handleDismiss} />;
}
